import React from "react";

/**
 * Phase J0.21 — Global ErrorBoundary.
 *
 * Wraps any sub-tree to convert React render errors / lifecycle exceptions
 * into a graceful fallback UI instead of unmounting the whole app (white
 * screen of death). Each boundary instance is independent, so a crash in
 * the project detail modal won't unmount the dashboard navbar.
 *
 * Usage:
 *   <ErrorBoundary scope="project-detail">
 *     <ProjectDetailModal ... />
 *   </ErrorBoundary>
 *
 * The boundary surfaces a "Réessayer" button that resets its internal state
 * — useful when the failure was due to a transient state (race condition
 * during a slow IA response, expired data structure, etc.).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const scope = this.props.scope || "unknown";
    // Iter 71 — Always surface the error to console.error (was console.log
    // before). In production this remains visible in browser DevTools for
    // user-side debugging without requiring a monitoring service.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${scope}]`, error, errorInfo);
    this.setState({ errorInfo });
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError({ error, errorInfo, scope });
      } catch (innerErr) {
        // Secondary error in onError handler — surface to console without
        // breaking the boundary itself.
        // eslint-disable-next-line no-console
        console.error(`[ErrorBoundary:${scope}] onError handler failed:`, innerErr);
      }
    }
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    const scope = this.props.scope || "section";
    return (
      <div
        className="m-4 rounded-xl border border-red-500/30 bg-red-500/5 p-5"
        data-testid={`error-boundary-${scope}`}
      >
        <h3 className="font-display text-base font-bold text-red-300 mb-1">
          Cette section n&apos;a pas pu s&apos;afficher
        </h3>
        <p className="text-xs text-slate-400 mb-3">
          <span className="mono text-red-300">{scope}</span> · Une erreur
          inattendue est survenue. Le reste de l&apos;application reste utilisable.
        </p>
        {process.env.NODE_ENV !== "production" && (
          <pre className="text-[10px] text-red-200 bg-black/40 rounded p-2 overflow-x-auto mb-3 max-h-32 overflow-y-auto">
            {error?.message || String(error)}
          </pre>
        )}
        <div className="flex gap-2">
          <button
            onClick={this.reset}
            className="btn-gold text-xs"
            data-testid={`error-boundary-retry-${scope}`}
          >
            Réessayer
          </button>
          {this.props.onReload && (
            <button
              onClick={this.props.onReload}
              className="btn-ghost text-xs"
            >
              Recharger les données
            </button>
          )}
        </div>
      </div>
    );
  }
}
